import { useToast } from '@/hooks/useToast'
import PhoneField from '@/molecules/FormikFields/PhoneField'
import SelectField from '@/molecules/FormikFields/SelectField'
import TextField from '@/molecules/FormikFields/TextField'
import { numEmployeesOptions } from '@/utils/constants/form'
import countries from '@/utils/enums/countries'
import { getDefaultPhoneCode } from '@/utils/functions/defaultPhoneCode'
import { isValidWorkEmail, phoneValidationSchema } from '@/utils/functions/form'
import clsx from 'clsx'
import { Form, Formik, FormikHelpers } from 'formik'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { object, string } from 'yup'
import type { BookADemoSectionData } from '@/data/inc/homepage-types'
import Image from 'next/image'

interface BookADemoProps {
  data?: BookADemoSectionData
}

const defaultBookADemoData: BookADemoSectionData = {
  heading:
    'Discover a new way to do content marketing in a quick 20-min call with Pepper',
  formTitle: 'Book a demo',
  imagePath: '/assets/inc/solutions/book-a-demo.png'
}

export const BookADemo = ({ data }: BookADemoProps) => {
  const bookADemoData = data ?? defaultBookADemoData

  return (
    <div className="grid md:grid-cols-2 gap-10 ">
      <div className="rounded-[42px] | relative">
        <Image
          fill
          src={
            bookADemoData.imagePath ?? '/assets/inc/solutions/book-a-demo.png'
          }
          alt=""
        />
      </div>
      <div className="p-6 md:p-10 border rounded-[42px] flex flex-col gap-8 | bg-[hsla(231,100%,99%,1)]">
        <h3 className="text-2xl md:text-3xl text-left font-medium">
          {bookADemoData.formTitle ?? 'Book a Demo'}
        </h3>
        <BookADemoForm />
      </div>
    </div>
  )
}

function BookADemoForm() {
  const router = useRouter()
  const { toast, showToast } = useToast()

  const handleSubmit = async (
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => {
    try {
      const result = await submitToMailChimp(
        `/api/forms/hp-book-a-demo/`,
        values
      )

      if (!result.ok) {
        showToast({
          title: result.data.message,
          type: 'error',
          swipeDirection: 'up'
        })
        return
      }
      helpers.resetForm()
      router.push({
        pathname: `/thank-you/`,
        query: { param: 'hp-book-a-demo-form' }
      })
    } catch (_e) {
      showToast({
        title: 'Please try again',
        type: 'error',
        swipeDirection: 'up'
      })
    }
  }

  const submitToMailChimp = async (url: string, values: FormValues) => {
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        ...values,
        status: 'subscribed',
        sourceUrl: router.asPath
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    })

    const data = await response.json()
    return { ok: response.ok, data }
  }

  return (
    <>
      <Formik<FormValues>
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        <Form
          name="Homepage Book A Demo Form"
          id="book-a-demo-hp-form"
          className="flex flex-col gap-6"
        >
          <div className="flex flex-col md:flex-row gap-5 justify-between">
            <TextField
              name="firstName"
              className={clsx('w-full')}
              placeholder="First Name"
              required
            />
            <TextField
              name="lastName"
              className={clsx('w-full')}
              placeholder="Last Name"
              required
            />
          </div>
          <TextField
            name="company"
            className={clsx('')}
            placeholder="Company Name"
            required
          />
          <SelectField
            name="numEmployees"
            placeholder="Number of Employees"
            options={numEmployeesOptions.map(item => ({
              name: item.name,
              value: item.value
            }))}
            className={clsx('relative')}
            required
          />
          <TextField
            name="email"
            className={clsx('')}
            placeholder="Email Address"
            required
          />
          <SelectField
            name="country"
            placeholder="Country"
            options={countries.map(country => ({
              name: country.name,
              value: country.name
            }))}
            className={clsx('relative')}
            required
          />
          <PhoneField
            name="phone"
            placeholder="9990000000"
            className=""
            required
          />
          <div className="py-4 w-fit">
            <button
              className={clsx(
                'text-lg font-bold text-white uppercase | rounded-full px-14 py-4 bg-inc-primary-30'
              )}
              type="submit"
            >
              Submit
            </button>
          </div>
          <div className="text-caption md:text-small text-center | max-w-col-3 md:max-w-col-5 mx-auto">
            By signing up you agree to our
            <Link
              href="/terms/#section-1"
              target="_blank"
              className="font-semibold underline underline-offset-4 mx-1"
            >
              Terms of Service
            </Link>
            and
            <Link
              href="/privacy/#section-1"
              target="_blank"
              className="font-semibold underline underline-offset-4 mx-1"
            >
              Privacy Policy
            </Link>
          </div>
        </Form>
      </Formik>
      {toast}
    </>
  )
}

type FormValues = {
  firstName: string
  lastName: string
  company: string
  numEmployees: string
  email: string
  country: string
  phone: {
    extension: string
    number: string
  }
}

const initialFormValues = {
  firstName: '',
  lastName: '',
  company: '',
  numEmployees: '',
  email: '',
  country: '',
  phone: {
    extension: getDefaultPhoneCode(),
    number: ''
  }
}

const validationSchema = object().shape({
  firstName: string().required('Please enter your first name'),
  lastName: string().required('Please enter your last name'),
  company: string().required('Please enter your company name'),
  numEmployees: string().required('Please select company size'),
  email: string()
    .email('Please enter work email address')
    .test('email', 'Please enter your work email address', value =>
      isValidWorkEmail(value ?? '')
    )
    .required('This field is required'),
  phone: phoneValidationSchema,
  country: string().required('Please select your country')
})
